import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Map } from 'immutable';
import { records as appStorageRecords } from '@saucelabs/sl-module-app-storage';
import { Button, IconButton } from '@saucelabs/ds-button';
import { Tooltip } from '@saucelabs/ds-tooltip';
import { IconInformationOutline, IconUserAddOutline } from '@saucelabs/ds-icons';
import { actions as modalActions } from '@saucelabs/sl-container-modal';

import { ValueOf } from '../../../globalTypes';
import { SESSION_TYPE } from '../../../constants';
import { records as realDeviceSelectionRecords } from '../../realDeviceSelection';
import { utils as mobileAppSelectionUtils } from '../../mobileAppSelection';
import MobileManualShareSessionModal, { MOBILE_MANUAL_SHARE_SESSION_MODAL_KEY } from '../../../components/MobileManualSession/MobileManualShareSessionModal';
import { VirtualSessionInviteModal, VIRTUAL_SESSION_INVITE_MODAL_KEY } from '../../liveSessionManager';
import constants from '../constants';
import selectors from '../selectors';

export type TopbarProps = {
  app: appStorageRecords.AppStorageFile | null;
  endSessionHandler: () => void;
  realDeviceSessionInfo?: realDeviceSelectionRecords.DeviceDescriptor;
  sessionInfoHander: () => void;
  sessionType: ValueOf<typeof SESSION_TYPE>;
  url?: string | null;
  virtualSessionInfo?: Map<string, unknown>;
};

const Topbar: React.FC<TopbarProps> = ({
  app,
  endSessionHandler,
  realDeviceSessionInfo,
  sessionInfoHander,
  sessionType,
  url,
  virtualSessionInfo,
}) => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const { device } = useSelector(selectors.toolbarButtonSelector);
  const isMobileRealSession = sessionType === SESSION_TYPE.MOBILE_REAL;
  let target: string;
  let environment: string;

  if (isMobileRealSession) {
    target = app ? mobileAppSelectionUtils.buildAppInfoString(app) : (url ?? '');
    environment = `${realDeviceSessionInfo?.name ?? ''} - ${realDeviceSessionInfo?.getPrettyOsAndOsVersion() ?? ''}`;
  } else {
    target = app ? mobileAppSelectionUtils.buildAppInfoString(app) : virtualSessionInfo?.get('url') as string;
    environment = (sessionType === SESSION_TYPE.DESKTOP) ?
      `${virtualSessionInfo?.get('browserDisplay') as string} ${virtualSessionInfo?.get('version') as string} - ${virtualSessionInfo?.get('os') as string}` :
      `${virtualSessionInfo?.get('browserDisplay') as string} - ${virtualSessionInfo?.get('osVersionDisplay') as string}`;
  }

  const showShareSessionModal = () => {
    void dispatch(modalActions.showModal(isMobileRealSession ?
      MOBILE_MANUAL_SHARE_SESSION_MODAL_KEY :
      VIRTUAL_SESSION_INVITE_MODAL_KEY,
    ));
  };
  const requestMobileShareLink = async () => {
    const sessionId = await device.share() as string;
    return `${window.location.href}/shared/${sessionId}`;
  };

  return (
    <div
      className="w-full px-2 bg-white flex items-center border-0 border-b border-solid border-gray-300"
      data-qa="live-session-topbar"
      style={{ height: constants.TOPBAR_HEIGHT_PX, minHeight: constants.TOPBAR_HEIGHT_PX }}
    >
      {isMobileRealSession && (
        <MobileManualShareSessionModal requestShareLink={requestMobileShareLink} />
      )}

      {!isMobileRealSession && (
        <VirtualSessionInviteModal />
      )}

      <div className="max-w-1/5 mr-1">
        <div className="flex font-bold">
          <Tooltip title={target} >
            <span className="block overflow-hidden overflow-ellipsis whitespace-nowrap">{target}</span>
          </Tooltip >
        </div>
        <div className="flex">
          <Tooltip title={environment}>
            <span className="block overflow-hidden overflow-ellipsis whitespace-nowrap">{environment}</span>
          </Tooltip >
        </div>
      </div>

      <div className="mr-auto flex">
        <Tooltip title="Session Info">
          <IconButton
            aria-label="Session Info"
            data-qa="live-session-topbar-session-info-button"
            icon={<IconInformationOutline />}
            onPress={sessionInfoHander}
            variant="text"
          />
        </Tooltip>

        <Tooltip title="Share Session">
          <IconButton
            aria-label="Share Session"
            data-qa="live-session-topbar-share-session-button"
            icon={<IconUserAddOutline />}
            onPress={showShareSessionModal}
            variant="text"
          />
        </Tooltip>
      </div>

      <Button
        color="danger"
        data-qa="live-session-topbar-end-session-button"
        onPress={endSessionHandler}
        variant="contained"
      >
        End Session
      </Button>
    </div>
  );
};

export default Topbar;
